import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../canopy-ui-components/api';
import { getIsBannerShown } from '../../canopy-ui-components/helpers/localStorage';
import { RootState } from '../store';

export type MenuType = {
    title: string;
    path: string;
    key: string;
    subMenu?: MenuType[];
};

export type MenuStructureData = {
    status: string | null;
    data: MenuStructure;
    error: string | null;
    homePageUrl: string | null;
};

export type MenuStructure = {
    sider: Array<MenuType>;
    navBar: Array<MenuType>;
    portfolio: Array<MenuType>;
    'excel-addin': Array<MenuType>;
    [key: string]: Array<MenuType | string>;
};

interface AppData {
    flags: {
        status: string | null;
        data: any;
        error: string | null;
    };
    menuStructure: MenuStructureData;
    portfolioUsers: Array<{ id: number; username: string }>;
    pathHistory: { current: string; previous: string };
    isBannerShown: boolean | null;
    allMenu: {
        loading: boolean | null;
        list: any;
    };
}

const initialState: AppData = {
    flags: {
        status: null,
        data: {
            features: {
                newFeature: true,
            },
            api: {
                login: 'login',
            },
        },
        error: null,
    },
    menuStructure: {
        status: null,
        data: {
            sider: [],
            navBar: [],
            portfolio: [],
            'excel-addin': [],
            pagesWithSubmenu: [],
            'tableau-analytics': [],
        },
        error: null,
        homePageUrl: null,
    },
    portfolioUsers: [],
    pathHistory: { current: '', previous: '' },
    isBannerShown: getIsBannerShown(),
    allMenu: {
        loading: null,
        list: null,
    },
};

export const fetchMenuStructure = createAsyncThunk(
    'appData/menuStructure',
    apiClient.getMenu.bind(apiClient)
);

export const fetchAllMenuThunk = createAsyncThunk(
    'appData/menu/all',
    async () => {
        const res = await apiClient.getAllMenu();
        return res;
    }
);

const isMenuExcluded = ({
    excludedOn,
    deviceType,
    isMicroFrontend,
}: Record<string, any>) => {
    return (
        excludedOn &&
        (excludedOn?.includes(deviceType) ||
            (isMicroFrontend && excludedOn?.includes('micro-fe')))
    );
};

const excludeMenus = ({ menuList = [], deviceType, isMicroFrontend }: any) => {
    return menuList?.reduce((acc: MenuType[], menu: any) => {
        const { name, path, sub_menu, ...rest } = menu;

        if (
            !isMenuExcluded({
                excludedOn: rest.excluded_on,
                deviceType,
                isMicroFrontend,
            })
        ) {
            acc.push({
                title: name,
                path: path?.startsWith('http') ? path : `/${path}`,
                subMenu: excludeMenus({
                    menuList: sub_menu,
                    deviceType,
                    isMicroFrontend,
                }),
                ...rest,
            });
        }
        return acc;
    }, []);
};
const allDataSlice = createSlice({
    name: 'appData',
    initialState: initialState,
    reducers: {
        setIsBannerShown(state: any, action: any) {
            state.isBannerShown = action.payload;
        },
        setPathHistory(state: any, action: any) {
            state.pathHistory = {
                current: action.payload,
                previous: state.pathHistory.current,
            };
        },
        clearMenu(state: AppData) {
            state.menuStructure = {
                status: null,
                data: {
                    sider: [],
                    navBar: [],
                    portfolio: [],
                    'excel-addin': [],
                    pagesWithSubmenu: [],
                    'tableau-analytics': [],
                },
                error: null,
                homePageUrl: null,
            };
        },
        addSubmenu(state: any, action: any) {
            state.menuStructure = {
                ...state.menuStructure,
                data: {
                    ...state.menuStructure.data,
                    [action.payload.type]: action.payload.data,
                },
            };
            if (action.payload.data.length > 0) {
                state.menuStructure.data.pagesWithSubmenu.push(
                    action.payload.type
                );
            } else {
                state.menuStructure.data.pagesWithSubmenu =
                    state.menuStructure.data.pagesWithSubmenu.filter(
                        (item: any) => item != action.payload.type
                    );
            }
        },
    },
    extraReducers: {
        [fetchMenuStructure.pending.type]: (state: AppData) => {
            state.menuStructure = {
                status: null,
                data: {
                    sider: [],
                    navBar: [],
                    portfolio: [],
                    'excel-addin': [],
                    pagesWithSubmenu: [],
                    'tableau-analytics': [],
                },
                error: null,
                homePageUrl: null,
            };
        },
        [fetchMenuStructure.fulfilled.type]: (
            state: AppData,
            action: PayloadAction<{ menus: any[]; deviceType: string }>
        ) => {
            const { deviceType, menus } = action.payload;
            const isMicroFrontend = Boolean((window as any).skeleton);
            const payload = excludeMenus({
                menuList: menus?.sort(
                    (a: any, b: any) =>
                        parseInt(a.position) - parseInt(b.position)
                ),
                deviceType,
                isMicroFrontend,
            });

            let sider = payload?.filter(
                (menu: any) => menu.type === 'menu-sidebar'
            );
            let navBar = payload?.filter(
                (menu: any) => menu.type === 'menu-navbar'
            );

            let subMenus = payload?.reduce((acc: any, item: any) => {
                const { subMenu, key } = item;
                if (subMenu?.length) {
                    acc[key] = subMenu;
                }
                return acc;
            }, {});

            const userProfileMenu = payload?.find(
                (m: any) => m.key === 'user-profile'
            );

            const userProfileSubMenus = userProfileMenu?.subMenu;

            subMenus = userProfileSubMenus
                ? userProfileSubMenus?.reduce((acc: any, item: any) => {
                      const { subMenu, key } = item;
                      if (subMenu?.length) {
                          acc[key] = subMenu;
                      }
                      return acc;
                  }, subMenus)
                : subMenus;

            state.menuStructure = {
                status: 'success',
                data: {
                    sider,
                    navBar,
                    pagesWithSubmenu: Object.keys(subMenus)?.map(
                        (subMenu: string) => subMenu
                    ),
                    ...subMenus,
                },
                error: null,
                homePageUrl: sider?.[0]?.path,
            };
        },
        [fetchMenuStructure.rejected.type]: (
            state: AppData,
            action: PayloadAction<string>
        ) => {
            state.menuStructure = {
                status: 'error',
                data: {
                    sider: [],
                    navBar: [],
                    portfolio: [],
                    'excel-addin': [],
                    pagesWithSubmenu: [],
                },
                error: action.payload,
                homePageUrl: null,
            };
        },
        [fetchAllMenuThunk.pending.type]: (
            state: AppData,
            action: PayloadAction<any>
        ) => {
            state.allMenu = {
                loading: true,
                list: null,
            };
        },
        [fetchAllMenuThunk.fulfilled.type]: (
            state: AppData,
            action: PayloadAction<any>
        ) => {
            state.allMenu = {
                loading: false,
                list: action.payload.map((menu: any) => {
                    const { id, menu_type, ...rest } = menu;
                    return {
                        ...rest,
                        menu_id: id,
                        type: menu_type,
                    };
                }),
            };
        },
        [fetchAllMenuThunk.rejected.type]: (
            state: AppData,
            action: PayloadAction<any>
        ) => {
            state.allMenu = {
                loading: false,
                list: null,
            };
        },
    },
});

export const selectAppData = (state: RootState): AppData => state.appData;
export const selectMenuStructure = (state: RootState): MenuStructureData =>
    state.appData.menuStructure;
export const selectAllMenuLoading = (state: RootState): boolean | null =>
    state.appData.allMenu.loading;
export const selectAllMenu = (state: RootState): any =>
    state.appData.allMenu.list;

export const { setPathHistory, clearMenu, addSubmenu, setIsBannerShown } =
    allDataSlice.actions;

export default allDataSlice;
